import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  Divider,
  Grid,
  Button,
  Skeleton,
  Avatar,
  useTheme,
  IconButton,
  Modal,
  Snackbar,
  SnackbarContent,
} from '@mui/material';
import {
  ArrowBack,
  CalendarToday,
  LocationOn,
  Link as LinkIcon,
  EventSeat,
  Lock,
  Public,
  WorkspacePremium,
  HowToReg,
  People,
  OpenInNew,
  ShareOutlined,
  Close as CloseIcon,
  Link as CopyLinkIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  WhatsApp as WhatsAppIcon,
  Check as CheckIcon,
  Visibility,
  Verified,
} from '@mui/icons-material';
import { FacebookShareButton, TwitterShareButton, LinkedinShareButton, WhatsappShareButton } from 'react-share';
import DOMPurify from 'dompurify';
import toast from 'react-hot-toast';
import { useAppDispatch, useAppSelector } from '@/store';
import { fetchEvent, enrollEvent } from '@/store';
import { useAuth } from '@/hooks';
import { USER_ROLES } from '@/constants/app';
import { EventOrganizer } from '@/constants/types';

const formatDateTime = (iso?: string) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', { dateStyle: 'long', timeStyle: 'short' });
};

export const EventDetailPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === USER_ROLES.SUPER_ADMIN;

  const { currentEvent: event, isLoading, isEnrolling } = useAppSelector((s) => s.events);

  const [shareOpen, setShareOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    if (eventId) dispatch(fetchEvent(eventId));
  }, [eventId, dispatch]);

  const handleEnroll = async () => {
    if (!eventId) return;
    try {
      await dispatch(enrollEvent(eventId)).unwrap();
      toast.success('Successfully enrolled!');
      dispatch(fetchEvent(eventId));
    } catch (err: any) {
      toast.error(err || 'Failed to enroll');
    }
  };

  const shareUrl = `https://hrtalky.in/events/${eventId}`;
  const shareTitle = event?.eventName || 'Check out this event on HRTalky';

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setSnackbarOpen(true);
      setShareOpen(false);
    });
  };

  const socialIcons = [
    { icon: <CopyLinkIcon />, label: 'Copy link', onClick: handleCopyLink },
    { component: FacebookShareButton, icon: <FacebookIcon />, label: 'Facebook', props: { hashtag: '#HRTalky' } },
    { component: TwitterShareButton, icon: <TwitterIcon />, label: 'X', props: { title: shareTitle, hashtags: ['HRTalky', 'Events'] } },
    { component: LinkedinShareButton, icon: <LinkedInIcon />, label: 'LinkedIn', props: { title: shareTitle, source: 'HRTalky' } },
    { component: WhatsappShareButton, icon: <WhatsAppIcon />, label: 'WhatsApp', props: { title: `${shareTitle}\n\n${shareUrl}`, separator: '' } },
  ];

  const sanitizedDescription = DOMPurify.sanitize(event?.description || '', {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 's', 'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'code', 'span'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'style'],
  });

  const organizer = event?.organizer && typeof event.organizer === 'object'
    ? (event.organizer as EventOrganizer)
    : null;

  if (isLoading || !event) {
    return (
      <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
        <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 3, mb: 3 }} />
        <Skeleton width="60%" height={40} sx={{ mb: 1 }} />
        <Skeleton width="40%" height={24} sx={{ mb: 3 }} />
        <Skeleton height={20} sx={{ mb: 1 }} />
        <Skeleton height={20} sx={{ mb: 1 }} />
        <Skeleton width="80%" height={20} />
      </Box>
    );
  }

  // ── Reusable styles ──────────────────────────────────────
  const sectionHeadingSx = {
    mb: 2.5,
    pl: 1.5,
    borderLeft: '4px solid',
    borderColor: 'primary.main',
    lineHeight: 1.2,
    fontWeight: 700,
  } as const;

  const sectionHeadingWithIconSx = {
    ...sectionHeadingSx,
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  } as const;

  const labelSx = {
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    fontSize: '0.68rem',
    fontWeight: 600,
    mb: 0.4,
  } as const;

  const infoCardSx = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 2,
    p: 2.5,
    bgcolor: 'background.paper',
    borderRadius: 2,
    border: `1px solid ${theme.palette.divider}`,
    height: '100%',
    transition: 'border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease',
    '&:hover': {
      borderColor: 'primary.main',
      boxShadow: theme.shadows[3],
      transform: 'translateY(-2px)',
    },
  } as const;

  const wideCardSx = {
    p: { xs: 2.5, md: 3 },
    bgcolor: 'background.paper',
    borderRadius: 2,
    border: `1px solid ${theme.palette.divider}`,
    transition: 'border-color 0.25s ease, box-shadow 0.25s ease',
    '&:hover': {
      borderColor: 'primary.main',
      boxShadow: theme.shadows[3],
    },
  } as const;

  return (
    <>
      <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, md: 3 } }}>
        {/* Back */}
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/events')}
          sx={{ mb: 2, textTransform: 'none', fontWeight: 600 }}
        >
          Back to Events
        </Button>

        {/* Banner */}
        {event.eventBanner?.url ? (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxHeight: { xs: 220, sm: 320, md: 380 },
              borderRadius: 3,
              overflow: 'hidden',
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.shadows[1],
              mb: 3,
            }}
          >
            <Box
              component="img"
              src={event.eventBanner.url}
              alt={event.eventName}
              sx={{
                display: 'block',
                width: '100%',
                maxHeight: { xs: 220, sm: 320, md: 380 },
                objectFit: 'cover',
              }}
            />
          </Box>
        ) : null}

        {/* Badges */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Chip
            label={event.category}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
          <Chip
            label={event.eventMode === 'online' ? 'Online' : 'In Person'}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
          <Chip
            label={event.ticketType === 'free' ? 'Free' : `Paid · ₹${event.price}`}
            color={event.ticketType === 'free' ? 'success' : 'warning'}
            size="small"
            sx={{ fontWeight: 600 }}
          />
          {event.eventVisibility === 'public' ? (
            <Chip icon={<Public sx={{ fontSize: 14 }} />} label="Public" size="small" variant="outlined" />
          ) : (
            <Chip icon={<Lock sx={{ fontSize: 14 }} />} label="Private" size="small" variant="outlined" />
          )}
          {event.certificateProvided && (
            <Chip
              icon={<WorkspacePremium sx={{ fontSize: 14 }} />}
              label="Certificate"
              size="small"
              color="success"
              variant="outlined"
            />
          )}
          {!event.isActive && (
            <Chip label="Inactive" size="small" color="default" />
          )}
        </Box>

        {/* Title row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
            mb: 2,
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              letterSpacing: -1,
              lineHeight: 1.05,
              fontSize: { xs: '1.75rem', sm: '2.25rem' },
            }}
          >
            {event.eventName}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isSuperAdmin && (
              <Box
                onClick={() => window.open(`/events/${eventId}/enrolled-users`, '_blank')}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: 'background.paper',
                  cursor: 'pointer',
                  flexShrink: 0,
                  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: theme.shadows[2],
                  },
                }}
              >
                <People sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2" fontWeight={600} color="text.secondary">
                  Enrolled Users
                </Typography>
                {event.enrollmentCount !== undefined && event.enrollmentCount > 0 && (
                  <Chip
                    label={event.enrollmentCount}
                    size="small"
                    color="primary"
                    sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
                  />
                )}
                <OpenInNew sx={{ fontSize: 14, color: 'text.disabled' }} />
              </Box>
            )}
            <IconButton
              size="small"
              onClick={() => setShareOpen(true)}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: 'background.paper',
                transition: 'border-color 0.2s ease',
                '&:hover': { borderColor: 'primary.main' },
              }}
            >
              <ShareOutlined fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Enroll button + enrollment count (non-super-admin) */}
        {!isSuperAdmin && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={event.isEnrolled ? <Verified /> : <HowToReg />}
              onClick={handleEnroll}
              disabled={isEnrolling || event.isEnrolled || !event.isActive}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 700,
                px: 3,
                py: 1.1,
                boxShadow: 'none',
                '&:hover': { boxShadow: theme.shadows[4] },
              }}
            >
              {isEnrolling ? 'Enrolling…' : event.isEnrolled ? 'Enrolled' : 'Interested to Join'}
            </Button>
            {event.enrollmentCount !== undefined && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 5,
                  bgcolor: 'background.paper',
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <People sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  {event.enrollmentCount} enrolled
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Key info heading */}
        <Typography variant="h6" sx={sectionHeadingSx}>
          Event Details
        </Typography>

        {/* Key info grid */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box sx={infoCardSx}>
              <CalendarToday sx={{ fontSize: 22, color: 'primary.main', mt: 0.2 }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={labelSx}>
                  Start
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {formatDateTime(event.startDateTime)}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {event.endDateTime && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={infoCardSx}>
                <CalendarToday sx={{ fontSize: 22, color: 'primary.main', mt: 0.2 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={labelSx}>
                    End
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {formatDateTime(event.endDateTime)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}

          {event.registrationDeadline && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={infoCardSx}>
                <CalendarToday sx={{ fontSize: 22, color: 'primary.main', mt: 0.2 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={labelSx}>
                    Registration Deadline
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {formatDateTime(event.registrationDeadline)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}

          {event.totalSeats && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={infoCardSx}>
                <EventSeat sx={{ fontSize: 22, color: 'primary.main', mt: 0.2 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={labelSx}>
                    Total Seats
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {event.totalSeats}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}

          {event.views !== undefined && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={infoCardSx}>
                <Visibility sx={{ fontSize: 22, color: 'primary.main', mt: 0.2 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={labelSx}>
                    Total Views
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {event.views}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>

        {/* Venue / Online link */}
        {event.eventMode === 'in_person' && event.venueName && (
          <>
            <Typography variant="h6" sx={sectionHeadingWithIconSx}>
              <LocationOn sx={{ fontSize: 22, color: 'primary.main' }} /> Venue
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
                p: { xs: 2, md: 2.5 },
                mb: 3,
                bgcolor: 'background.paper',
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                transition: 'border-color 0.25s ease, box-shadow 0.25s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: theme.shadows[3],
                },
              }}
            >
              <LocationOn sx={{ fontSize: 22, color: 'primary.main', mt: 0.2 }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body1" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                  {event.venueName}
                </Typography>
                {event.fullAddress && (
                  <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word', mt: 0.5 }}>
                    {[
                      event.fullAddress.addressLine1,
                      event.fullAddress.addressLine2,
                      event.fullAddress.city,
                      event.fullAddress.state,
                      event.fullAddress.pincode,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </Typography>
                )}
                {(event.fullAddress?.googleMapsLink || (event as any).googleMapsLink) && (
                  <Button
                    variant="outlined"
                    size="small"
                    href={event.fullAddress?.googleMapsLink || (event as any).googleMapsLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      mt: 1.5,
                      textTransform: 'none',
                      borderRadius: 2,
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Get Directions
                  </Button>
                )}
              </Box>
            </Box>
          </>
        )}

        {event.eventMode === 'online' && event.eventLink && (
          <Box sx={{ mb: 3 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<LinkIcon />}
              href={event.eventLink}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                fontWeight: 700,
                px: 4,
                py: 1.2,
                boxShadow: 'none',
                '&:hover': { boxShadow: theme.shadows[4] },
              }}
            >
              Join the Event
            </Button>
          </Box>
        )}

        <Divider sx={{ mb: 3 }} />

        {/* About */}
        <Typography variant="h6" sx={sectionHeadingSx}>
          About this Event
        </Typography>
        <Box sx={{ ...wideCardSx, mb: 3 }}>
          <Box
            sx={{
              lineHeight: 1.8,
              color: 'text.secondary',
              '& p': { margin: '0.5em 0', fontSize: '0.95rem' },
              '& h1': { fontSize: '1.6em', fontWeight: 700, margin: '0.67em 0', color: 'text.primary' },
              '& h2': { fontSize: '1.35em', fontWeight: 700, margin: '0.75em 0', color: 'text.primary' },
              '& h3': { fontSize: '1.15em', fontWeight: 700, margin: '0.83em 0', color: 'text.primary' },
              '& h4, & h5, & h6': { fontWeight: 700, margin: '1em 0', color: 'text.primary' },
              '& blockquote': {
                borderLeft: `3px solid ${theme.palette.primary.main}`,
                pl: 2,
                py: 0.5,
                color: 'text.secondary',
                fontStyle: 'italic',
                my: 1.5,
              },
              '& code': {
                backgroundColor: theme.palette.mode === 'dark' ? '#2d2d2d' : '#f5f5f5',
                padding: '2px 6px',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '0.9em',
              },
              '& a': {
                color: theme.palette.primary.main,
                textDecoration: 'none',
                fontWeight: 500,
                '&:hover': { textDecoration: 'underline' },
              },
              '& ul, & ol': { paddingLeft: '1.5em', margin: '0.5em 0' },
              '& li': { margin: '0.3em 0' },
              '& strong': { fontWeight: 700, color: 'text.primary' },
            }}
            dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
          />
        </Box>

        {/* Organizer */}
        {organizer && (
          <>
            <Typography variant="h6" sx={sectionHeadingSx}>
              Organizer
            </Typography>
            <Box sx={{ ...wideCardSx, mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  bgcolor: 'primary.main',
                  width: 52,
                  height: 52,
                  fontWeight: 700,
                }}
              >
                {organizer.name?.charAt(0).toUpperCase() || 'O'}
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={labelSx}
                >
                  Organizer
                </Typography>
                <Typography variant="subtitle1" fontWeight={700} noWrap>
                  {organizer.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {organizer.email}
                </Typography>
              </Box>
            </Box>
          </>
        )}

        {/* Hosted By */}
        {event.businessId && typeof event.businessId === 'object' && (event.businessId as any).businessName && (
          <>
            <Typography variant="h6" sx={sectionHeadingSx}>
              Hosted By
            </Typography>
            <Box
              onClick={() => navigate(`/businesses/${(event.businessId as any)._id}`)}
              sx={{
                ...wideCardSx,
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                cursor: 'pointer',
              }}
            >
              <Avatar
                sx={{
                  bgcolor: 'secondary.main',
                  width: 52,
                  height: 52,
                  fontWeight: 700,
                }}
              >
                {(event.businessId as any).businessName?.charAt(0).toUpperCase() || 'B'}
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={labelSx}
                >
                  Business
                </Typography>
                <Typography variant="subtitle1" fontWeight={700} noWrap>
                  {(event.businessId as any).businessName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  View Business Profile
                </Typography>
              </Box>
              <OpenInNew sx={{ color: 'text.secondary', flexShrink: 0 }} />
            </Box>
          </>
        )}
      </Box>

      {/* Share Modal */}
      <Modal open={shareOpen} onClose={() => setShareOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 340,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 3,
            borderRadius: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>
              Share Event
            </Typography>
            <IconButton size="small" onClick={() => setShareOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box>
            {socialIcons.map((social, i) => {
              const rowSx = {
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1,
                borderRadius: 2,
                mb: 0.5,
                cursor: 'pointer',
                width: '100%',
                '&:hover': { bgcolor: 'action.hover' },
              };
              if (social.onClick) {
                return (
                  <Box key={i} onClick={social.onClick} sx={rowSx}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 24,
                        height: 24,
                        flexShrink: 0,
                      }}
                    >
                      {social.icon}
                    </Box>
                    <Typography variant="body2">{social.label}</Typography>
                  </Box>
                );
              }
              const ShareBtn = social.component!;
              return (
                <Box key={i} sx={{ ...rowSx, p: 0 }}>
                  <ShareBtn
                    url={shareUrl}
                    {...(social.props || {})}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '8px 16px',
                      width: '100%',
                      color: 'inherit',
                      textDecoration: 'none',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 24,
                        height: 24,
                        flexShrink: 0,
                      }}
                    >
                      {social.icon}
                    </Box>
                    <Typography variant="body2">{social.label}</Typography>
                  </ShareBtn>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Modal>

      {/* Copy snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <SnackbarContent
          sx={{ bgcolor: 'success.main', color: 'white', borderRadius: 2 }}
          message={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon />
              <Typography variant="body2" sx={{ color: 'white' }}>
                Link copied to clipboard!
              </Typography>
            </Box>
          }
          action={
            <IconButton onClick={() => setSnackbarOpen(false)} color="inherit" size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        />
      </Snackbar>
    </>
  );
};
